import { useHandleHttpError } from '../../../../../lib/hooks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, FormikErrors } from 'formik';
import type { UploadPublicCertificateProps } from './types';
import { getGuid, BulmaType, ButtonProps, Form, Input, TaskPanel, useToast } from '@easylife365/react';
import { arrayBufferToBase64 } from '../../../../../services/utils';
import * as Yup from 'yup';
import { faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { KeyCredentialDto } from '../../../../../api/graph.types';
import { useUpdateKeyCredentials } from '../../../../../api/app.api';

type SetFieldValue = (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<{}>>;

const initialValues: KeyCredentialDto = {
  type: 'AsymmetricX509Cert',
  usage: 'Verify',
};

const allowedExtensions = ['.cer', '.pem', '.crt'];

const UploadPublicCertificate: React.FC<UploadPublicCertificateProps> = ({ app, isOpen, close }) => {
  const { t } = useTranslation('addCertificatePanel');
  const { t: tCommon } = useTranslation('common');
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        key: Yup.string().required(t('validation.certificateRequired')),
      }),
    [t]
  );
  const { toastError } = useToast();
  const handleHttpError = useHandleHttpError();
  const { mutateAsync: updateKeyCredentials } = useUpdateKeyCredentials(app.resourceType);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, setFieldValue: SetFieldValue) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      toastError(t('toasts.invalidFile'), t('toasts.invalidFileCer'));
      return;
    }

    const fileExtension = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(fileExtension)) {
      toastError(t('toasts.invalidType'), t('toasts.invalidTypePublic'));
      return;
    }

    const reader = new FileReader();
    reader.readAsArrayBuffer(selectedFile);
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        try {
          const uint8Array = new Uint8Array(reader.result);
          const base64Cert = arrayBufferToBase64(uint8Array);
          setFieldValue('key', base64Cert, true);
        } catch (error) {
          console.error('Error processing the file:', error);
          toastError(t('toasts.processingError'), t('toasts.processingErrorMessage'));
        }
      } else {
        toastError(t('toasts.invalidFormat'), t('toasts.invalidFormatMessage'));
      }
    };

    reader.onerror = () => {
      console.error('Error reading the file.');
      toastError(t('toasts.readError'), t('toasts.readErrorMessage'));
    };
  };

  const onSubmit = async (values: KeyCredentialDto) => {
    try {
      await updateKeyCredentials({
        id: app.id,
        request: {
          appType: app.resourceType,
          keyCredentials: [
            ...app.keyCredentials,
            {
              ...values,
              keyId: getGuid(),
            },
          ],
        },
      });
      close();
    } catch (error) {
      handleHttpError(error);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnMount
    >
      {({ isSubmitting, isValid, setFieldValue, handleSubmit }) => {
        const ctaButtons: ButtonProps[] = [
          {
            bulmaType: BulmaType.Primary,
            disabled: !isValid || isSubmitting,
            text: t('buttons.add'),
            icon: faWandMagicSparkles,
            loading: isSubmitting,
            type: 'submit',
          },
        ];

        return (
          <form onSubmit={handleSubmit}>
            <TaskPanel title={t('uploadPublic.title')} isVisible={isOpen} ctaButtons={ctaButtons} onCancel={close}>
              <Form transparent={true}>
                <Input
                  formLabel={t('fields.certificate')}
                  helpMessage={t('uploadPublic.help')}
                  required
                  type="file"
                  accept={allowedExtensions.join(',')}
                  onChange={(e) => handleFileChange(e, setFieldValue)}
                />
                <Input
                  formLabel={tCommon('fields.description')}
                  name="displayName"
                  placeholder={t('fields.descriptionPlaceholder')}
                />
              </Form>
            </TaskPanel>
          </form>
        );
      }}
    </Formik>
  );
};

export default UploadPublicCertificate;
